/**
 * WearablesPage.jsx — iter876
 *
 * "Настройки → Носими устройства" page. Users can connect / disconnect /
 * sync their Google Health account so the Sleep Twin and Body-Composition
 * Twin receive real-world data.
 *
 * OAuth flow:
 *   1. User clicks "Свържи Google Health" → GET /api/wearables/google/login
 *      returns a Google authorization URL → we navigate the browser to it.
 *   2. After consent, Google redirects to /api/wearables/google/callback,
 *      which then redirects back here with `?connected=google_health` or
 *      `?error=...`.
 *   3. On mount we check status via /api/wearables/google/status and
 *      display the last-sync timestamp; a manual "Синхронизирай сега"
 *      button triggers /api/wearables/google/sync.
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Watch, Loader2, CheckCircle2, XCircle, RefreshCw, LinkIcon, Unlink, Info, ArrowLeft } from "lucide-react";
import { useAuth } from "@/App";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ERROR_MESSAGES_BG = {
  access_denied:            "Отказахте достъп на Google — можете да опитате отново.",
  invalid_state:            "Изтекла или невалидна сесия. Опитайте отново.",
  state_expired:            "Заявката за свързване изтече (>10 мин). Опитайте отново.",
  missing_params:           "Google не върна необходимите параметри. Опитайте отново.",
  token_exchange_failed:    "Google отказа размяната на код за токен. Проверете разрешенията.",
  no_refresh_token:         "Google не издаде refresh token. Прекратете достъпа от Google акаунт → Сигурност и опитайте отново.",
  encryption_unavailable:   "Сървърът не може да съхрани токена безопасно — свържете се с поддръжка.",
};

function formatISO(dt) {
  if (!dt) return "—";
  try {
    return new Date(dt).toLocaleString("bg-BG", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return dt;
  }
}

export default function WearablesPage() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [connectLoading, setConnectLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  const fetchStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      const { data } = await axios.get(`${API}/wearables/google/status`);
      setStatus(data);
    } catch (e) {
      // 401 means logged-out — parent ProtectedRoute handles redirect.
      setStatus({ connected: false, provider: "google_health" });
    } finally {
      setStatusLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Read redirect params from OAuth callback (?connected=... or ?error=...)
  useEffect(() => {
    const connected = params.get("connected");
    const error = params.get("error");
    if (connected === "google_health") {
      toast.success("Google Health свързан успешно!");
      // Kick off an initial sync so the UI shows data right away.
      handleSync();
      setParams({}, { replace: true });
    } else if (error) {
      const msg = ERROR_MESSAGES_BG[error] || `Грешка при свързване: ${error}`;
      toast.error(msg);
      setParams({}, { replace: true });
    }
  }, []);

  const handleConnect = async () => {
    setConnectLoading(true);
    try {
      const { data } = await axios.get(`${API}/wearables/google/login`);
      if (data?.auth_url) {
        window.location.href = data.auth_url;
      } else {
        toast.error("Неочакван отговор от сървъра.");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Неочаквана грешка";
      toast.error(`Свързването пропадна: ${detail}`);
    } finally {
      setConnectLoading(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm("Сигурни ли сте, че искате да прекратите връзката с Google Health? Ще спрем да получаваме данни от Вашия акаунт.")) return;
    try {
      await axios.post(`${API}/wearables/google/disconnect`);
      toast.success("Google Health е откачен.");
      setStatus({ connected: false, provider: "google_health" });
      setSyncResult(null);
    } catch (e) {
      toast.error("Неуспешно откачване.");
    }
  };

  const handleSync = async () => {
    setSyncLoading(true);
    try {
      const { data } = await axios.post(`${API}/wearables/google/sync`);
      setSyncResult(data);
      await fetchStatus();
      toast.success("Синхронизацията е завършена.");
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Синхронизацията пропадна";
      toast.error(detail);
    } finally {
      setSyncLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-slate-800 dark:bg-slate-950 dark:text-slate-200">
      <main className="mx-auto max-w-3xl px-4 py-8" data-testid="wearables-page-root">
        <Link
          to="/dashboard/profile"
          className="mb-4 inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800"
          data-testid="wearables-back"
        >
          <ArrowLeft className="h-4 w-4" /> Обратно към профил
        </Link>

        <header className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Watch className="h-6 w-6 text-pink-500" />
            <h1 className="text-3xl font-bold">Носими устройства</h1>
          </div>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Свържете Google Health, за да могат Sleep Twin и Body-Composition Twin да използват реалните
            Ви данни за сън, стъпки, сърдечна честота и активни минути. Никакви данни не се използват без
            изричното Ви разрешение.
          </p>
        </header>

        {/* Google Health card */}
        <section
          className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-slate-900/40"
          data-testid="wearables-google-card"
        >
          <div className="flex items-center justify-between gap-4 border-b border-slate-100 pb-3 dark:border-white/5">
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-red-400 via-yellow-400 to-green-400 flex items-center justify-center text-white font-bold">
                G
              </div>
              <div>
                <h2 className="text-lg font-bold" data-testid="wearables-google-title">Google Health</h2>
                <p className="text-xs text-slate-500">Сън · стъпки · пулс · активни минути</p>
              </div>
            </div>
            <div>
              {statusLoading ? (
                <span className="inline-flex items-center gap-1 text-xs text-slate-400">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Зареждане…
                </span>
              ) : status?.connected ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-bold text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300" data-testid="wearables-google-status-connected">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Свързан
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-bold text-slate-600 dark:bg-slate-800 dark:text-slate-300" data-testid="wearables-google-status-disconnected">
                  <XCircle className="h-3.5 w-3.5" /> Не е свързан
                </span>
              )}
            </div>
          </div>

          {status?.connected && (
            <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
              <dt className="text-slate-500">Свързан на</dt>
              <dd className="font-mono" data-testid="wearables-connected-at">{formatISO(status.connected_at)}</dd>
              <dt className="text-slate-500">Последна синхронизация</dt>
              <dd className="font-mono" data-testid="wearables-last-sync">{formatISO(status.last_sync_at)}</dd>
              <dt className="text-slate-500">Обхвати</dt>
              <dd className="text-xs text-slate-600 dark:text-slate-400">
                {(status.scopes || []).length}
                {(status.scopes || []).length ? " активни" : " (не са върнати от Google)"}
              </dd>
            </dl>
          )}

          {syncResult && (
            <div className="mt-4 rounded-lg bg-slate-50 p-4 text-sm dark:bg-slate-800/50" data-testid="wearables-sync-summary">
              <p className="mb-2 font-bold text-slate-700 dark:text-slate-200">Обобщение (последни 7 дни)</p>
              <div className="grid grid-cols-2 gap-x-6 gap-y-1">
                <span className="text-slate-500">Стъпки</span>
                <span className="font-mono">{syncResult.steps_7d ?? "—"}</span>
                <span className="text-slate-500">Активни минути</span>
                <span className="font-mono">{syncResult.active_minutes_7d ?? "—"}</span>
                <span className="text-slate-500">Среден пулс</span>
                <span className="font-mono">{syncResult.avg_hr_7d ?? "—"} bpm</span>
                <span className="text-slate-500">Общо сън</span>
                <span className="font-mono">{syncResult.sleep_hours_7d ?? "—"} ч.</span>
                <span className="text-slate-500">Оценка за сън</span>
                <span className="font-mono">{syncResult.sleep_quality_score ?? "—"} / 100</span>
              </div>
            </div>
          )}

          <div className="mt-4 flex flex-wrap gap-2">
            {status?.connected ? (
              <>
                <button
                  onClick={handleSync}
                  disabled={syncLoading}
                  className="inline-flex items-center gap-2 rounded-lg bg-pink-600 px-4 py-2 text-sm font-bold text-white shadow hover:bg-pink-700 disabled:opacity-60"
                  data-testid="wearables-sync-btn"
                >
                  {syncLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                  Синхронизирай сега
                </button>
                <button
                  onClick={handleDisconnect}
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-white/10 dark:text-slate-200 dark:hover:bg-white/5"
                  data-testid="wearables-disconnect-btn"
                >
                  <Unlink className="h-4 w-4" /> Прекрати връзката
                </button>
              </>
            ) : (
              <button
                onClick={handleConnect}
                disabled={connectLoading || !user}
                className="inline-flex items-center gap-2 rounded-lg bg-pink-600 px-4 py-2 text-sm font-bold text-white shadow hover:bg-pink-700 disabled:opacity-60"
                data-testid="wearables-connect-btn"
              >
                {connectLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <LinkIcon className="h-4 w-4" />}
                Свържи Google Health
              </button>
            )}
          </div>
        </section>

        {/* Privacy + info */}
        <aside className="mt-8 rounded-xl border border-blue-100 bg-blue-50/60 p-4 text-xs text-blue-900 dark:border-blue-500/20 dark:bg-blue-500/5 dark:text-blue-200">
          <div className="flex items-start gap-2">
            <Info className="h-4 w-4 shrink-0" />
            <div className="space-y-1">
              <p><strong>Данни, които събираме:</strong> сън (продължителност + качество), стъпки, активни минути, среден пулс — за последните 7 дни.</p>
              <p><strong>Как ги съхраняваме:</strong> refresh token е криптиран (AES-128 Fernet); агрегираните метрики попадат в <code>health_conditions.external_sources.google_health</code>.</p>
              <p><strong>Как да прекратите:</strong> с бутона по-горе, или директно от Google акаунт → Сигурност → Приложения с достъп.</p>
              <p><strong>Съответствие:</strong> GDPR Art. 6(1)(a) — изрично съгласие; Art. 7 — можете да го оттеглите по всяко време.</p>
            </div>
          </div>
        </aside>
      </main>
    </div>
  );
}
